import { mapLanguageToLocale } from '../../utils/mapLanguageToLocale';
import { selectors as authSelectors } from '../auth.duck';
import { selectors as i18nSelectors } from '../i18n';
import { getSupportedLocales } from './getSupportedLocales';

import type { SupportedLocale } from '../../utils/numberFormat';

/**
 * Combined selector that derives the numeric locale from auth + i18n state.
 *
 * Priority:
 *  1. The `locale` field on the authenticated user (whoami), if it is a
 *     supported Intl locale.
 *  2. The app language mapped to a BCP 47 locale via `mapLanguageToLocale`.
 *
 * A `locale` that arrives from the whoami but is not in the supported list is
 * logged (not silently discarded) so unexpected/unsupported values surface
 * instead of falling back mutely to the language-derived locale.
 */
export function getLocale(state: unknown): SupportedLocale {
  const whoamiLocale = authSelectors.getUserLocale(state);

  if (whoamiLocale) {
    const supportedLocales = getSupportedLocales();
    if (supportedLocales.includes(whoamiLocale)) return whoamiLocale;
    // eslint-disable-next-line no-console
    console.warn(
      `[getLocale] Unsupported user locale "${whoamiLocale}" received from whoami; falling back to the language-derived locale.`,
    );
  }

  const lang = i18nSelectors.getLanguage(state);
  return mapLanguageToLocale(lang);
}
